import { useInfiniteQuery } from '@tanstack/react-query';
import { logUnknownErrorResponse } from '@hungryroot/o11y';
import {
  StatusCode,
  getProductsRequest,
  type Product as ProductV2,
} from '@hungryroot/rest';
import { useIdentify } from '@hungryroot/rest-hooks';

export { type ProductV2 };

type OptionType = {
  enabled?: boolean;
};

const TIME_LIMIT_15_MINUTES = 15 * 60 * 1000;

export function useProducts(params: {
  options: OptionType;
  queryParams: URLSearchParams;
}) {
  const { queryParams, options } = params;

  const customer = useIdentify();
  const customerId = customer?.customerData?.id;

  options.enabled = Boolean(options.enabled && customerId && customerId > 0);

  if (
    customerId &&
    (queryParams.has('is_recent') || queryParams.has('is_favorite'))
  ) {
    queryParams.set('customer_id', customerId.toString());
  }

  return useInfiniteQuery({
    queryKey: ['ProductsV2', queryParams.toString()],
    queryFn: async ({ pageParam }) => {
      const newParams = new URLSearchParams(queryParams.toString());
      newParams.set('offset', pageParam.toString());
      const res = await getProductsRequest(newParams);

      if (res.status !== StatusCode.SuccessOK) {
        logUnknownErrorResponse('Failed to fetch products', {
          key: 'useProducts',
          error: JSON.stringify(res.data, null, 2),
          queryParams: newParams.toString(),
        });

        throw new Error(`${res.data}`);
      }
      return res.data;
    },
    ...options,
    getNextPageParam: (lastPage, pages) => {
      return pages.reduce((accum, page) => {
        if (page?.results) {
          return accum + page.results.length;
        }
        return accum;
      }, 0);
    },
    initialPageParam: 0,
    gcTime: TIME_LIMIT_15_MINUTES,
    refetchInterval: TIME_LIMIT_15_MINUTES,
    staleTime: TIME_LIMIT_15_MINUTES,
    retry: 0,
  });
}
